import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI, videoAPI, onboardingAPI, aiAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../lib/i18n';
import { track } from '../lib/analytics';
import { markOnboardingComplete } from '../lib/onboarding';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import RCLogo from '../components/Logo';
import {
  Sparkles, ArrowRight, Loader2, CheckCircle2, Play, Wand2,
  Zap, Database, Users, Image as ImageIcon, Upload, X, Pencil,
  Link as LinkIcon, UserCircle2,
} from 'lucide-react';
import WillhabenImportFlow from '../components/WillhabenImportFlow';
import PropertyQuickEditModal from '../components/PropertyQuickEditModal';

/**
 * FirstRenderPage. The "wow in 60 seconds" v1.5 onboarding.
 *
 * The whole page exists to funnel the user into one obvious action:
 * **render the demo property as a video** with a single click. Nothing
 * else. No menu, no skip, no "set up Justimmo first." That's the
 * radical simplification the audit + product owner agreed on.
 *
 * State machine, top-to-bottom:
 *
 *   `loading`    . Bootstrapping (ensure demo property + fetch templates)
 *   `ready`      . Demo property loaded, template grid visible, waiting
 *                   for the user to click a card
 *   `rendering`  . Render fired, full-screen hype overlay locks the
 *                   viewport and polls Creatomate for status
 *   `done`       . Render succeeded, full-screen result with the video
 *                   auto-playing and the upgrade ladder visible below
 *   `error`      . Something went wrong before render started
 *
 * Anti-abuse gates (`email_not_verified`, `card_on_file_required`)
 * surface as toasts via the global axios interceptor. The user lands
 * back in `ready` state and can retry after verifying.
 *
 * This page replaces the old 7-step OnboardingWizard for free-tier
 * accounts. The wizard's other steps (Justimmo, social, music,
 * voiceover) get re-surfaced contextually elsewhere. When the user
 * is ready for them, not as a single big setup tax.
 */

// We surface ALL renderable templates here so the user sees the full
// breadth of what they're getting on the paid plan. Earlier we curated
// to 5 to "reduce choice paralysis" but the product owner's read is the
// opposite: more visible templates = more "ooh which one should I try"
// = more renders attempted = better conversion. Templates that need
// more images than the current property has are filtered out at render
// time (see `renderableTemplates` below) so the user only sees
// thumbnails for templates that can actually render.
//
// Preferred order. Pulled to the top of the list so the most striking
// templates are above the fold. Anything not in this list gets appended
// after, in API order.
const FIRST_RENDER_PREFERRED_ORDER = [
  'tpl-plakat',
  'tpl-noir-luxus',
  'tpl-bold-new-listing',
  'tpl-cover-editorial',
  'tpl-glass-panel',
  'tpl-nordic-minimal',
  'tpl-rotband',
  'tpl-feature-spotlight',
  'tpl-klebeband-collage',
  'tpl-agent-story',
  'tpl-beton-brutal',
  'tpl-skyline',
  'tpl-daylight',
  'tpl-grid-showcase',
  'tpl-preisreduzierung',
];

// Hype copy that rotates through the rendering overlay so the user
// has something to read while Creatomate works (~30-90s).
const RENDER_HYPE_LINES = (t) => [
  t('firstRender.hype1') || 'KI sucht die schönsten Aufnahmen aus…',
  t('firstRender.hype2') || 'Voiceover wird mit deutscher Stimme erstellt…',
  t('firstRender.hype3') || 'Animationen + Branding werden zusammengesetzt…',
  t('firstRender.hype4') || 'Letzte Politur. Fast fertig…',
];

export default function FirstRenderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading');
  const [demoProperty, setDemoProperty] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Render-state.
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [renderId, setRenderId] = useState(null);
  const [renderStatus, setRenderStatus] = useState('');
  const [renderProgress, setRenderProgress] = useState(0);
  const [resultVideo, setResultVideo] = useState(null);
  // True iff this render was the user's free *demo*. Backend's
  // /generate/video returns is_first_render=true on the very first
  // render of a free-tier account. The done-screen below switches to
  // demo-framing copy + "add your photo + brand" CTA when this is set.
  const [wasDemoRender, setWasDemoRender] = useState(false);

  // For the rotating hype copy during render.
  const [hypeIdx, setHypeIdx] = useState(0);
  const hypeLines = RENDER_HYPE_LINES(t);

  // Brokenness in template demo videos. Fall back to thumbnail.
  const [brokenDemoVideos, setBrokenDemoVideos] = useState(new Set());

  // ── Mode toggle: demo / manual / willhaben ───────────────────────
  // demo:      auto-loaded sample property → zero-friction wow moment
  // manual:    user fills the form themselves with their own photos
  // willhaben: user pastes their willhaben.at listing URL → we scrape
  //            title, price, address, highlights + all photos, they
  //            confirm + pick which photos to use → property saved
  //
  // Willhaben is by far the most common path for Maklers in Austria –
  // most agents have their listings on willhaben anyway, so paste-and-
  // import skips ~5 minutes of typing + photo upload vs. the manual
  // track. Keeping all three modes side-by-side preserves the demo
  // path for users without an existing listing.
  const [mode, setMode] = useState('demo'); // 'demo' | 'manual' | 'willhaben'
  // Filled in when the user submits the manual form. After save,
  // takes the place of demoProperty as the render target.
  const [manualProperty, setManualProperty] = useState(null);
  // Filled in once the user confirms a willhaben import. Same shape
  // as manualProperty. Tracked separately so switching mode tabs
  // doesn't lose either one's progress.
  const [willhabenProperty, setWillhabenProperty] = useState(null);
  // Quick-edit modal state. Lets the user tweak title/price/highlights
  // /description on a saved property before rendering. Most relevant
  // after a Willhaben import where the auto-scraped title is often
  // SEO-spam-style and needs cleanup.
  const [editingProperty, setEditingProperty] = useState(null);
  // Form state for the manual track.
  const [manualForm, setManualForm] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    highlights: '',  // comma-separated; split on save
    images: [],
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [submittingProperty, setSubmittingProperty] = useState(false);
  // AI description: single-use per page mount. Frontend cap stops the
  // honest user from accidentally double-clicking; backend rate-limit
  // (10/h per IP) handles malicious cases.
  const [aiLoading, setAiLoading] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const fileInputRef = useRef(null);

  // Whatever property the render will fire against. Resolves to the
  // mode-specific source: demo → demoProperty, manual → manualProperty
  // (after form save), willhaben → willhabenProperty (after import).
  // Until the user-side property is saved/imported, `null` and the
  // templates grid is dimmed + un-clickable.
  const currentProperty =
    mode === 'demo' ? demoProperty
    : mode === 'manual' ? manualProperty
    : willhabenProperty;
  // Filter templates by media_count vs the property's actual image
  // count. We don't want the user clicking a template that needs 10
  // images when their property only has 4. Server would 400, the user
  // would feel cheated. Better to just hide it. Templates with
  // `media_count` unset or 0 are passed through (no constraint).
  const propertyImgCount = currentProperty?.images?.length || 0;
  const renderableTemplates = templates.filter(tpl => {
    const need = Number(tpl.media_count) || 0;
    return need === 0 || need <= propertyImgCount;
  });

  // Polling cleanup
  const pollTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  // Bootstrap on mount: create/fetch the demo property AND fetch the
  // template catalogue. Both in parallel because they're independent.
  useEffect(() => {
    (async () => {
      try {
        const [propRes, tplRes] = await Promise.all([
          propertiesAPI.ensureDemo(),
          videoAPI.getTemplates(),
        ]);
        if (!mountedRef.current) return;
        setDemoProperty(propRes.data);
        const all = tplRes.data || [];
        // Show ALL templates; sort the preferred ones to the top so
        // the strongest visuals are above the fold. Templates not in
        // FIRST_RENDER_PREFERRED_ORDER get appended after, in the
        // order the API returned them.
        const preferred = FIRST_RENDER_PREFERRED_ORDER
          .map(id => all.find(tpl => tpl.id === id))
          .filter(Boolean);
        const rest = all.filter(
          tpl => !FIRST_RENDER_PREFERRED_ORDER.includes(tpl.id)
        );
        setTemplates([...preferred, ...rest]);
        setPhase('ready');
        track('first_render_loaded');
      } catch (e) {
        if (!mountedRef.current) return;
        setPhase('error');
        setErrorMessage(
          e.response?.data?.detail
          || t('firstRender.bootstrapFailed')
          || 'Konnte Demo nicht laden. Bitte Seite neu laden.'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotate hype copy every 6s during render so the screen feels alive
  // instead of stuck. Stops the moment phase leaves `rendering`.
  useEffect(() => {
    if (phase !== 'rendering') return;
    const id = setInterval(() => {
      setHypeIdx(i => (i + 1) % hypeLines.length);
    }, 6000);
    return () => clearInterval(id);
  }, [phase, hypeLines.length]);

  // Kick off the render for a chosen template. The video gen API
  // accepts just property_id + template_id + language. We deliberately
  // don't expose voice/music/manual-mode picks in the first-render UI
  // because every additional choice is friction. Defaults are good
  // enough for a wow demo.
  const handleRender = async (template) => {
    // currentProperty resolves to demoProperty in 'demo' mode and
    // manualProperty in 'manual' mode (after the user has saved
    // their form). Defensive bail if not ready.
    const targetProperty = currentProperty;
    if (!targetProperty) {
      toast.error(t('firstRender.noPropertyYet')
        || 'Bitte zuerst eine Immobilie auswählen oder anlegen.');
      return;
    }
    setActiveTemplate(template);
    setPhase('rendering');
    setRenderProgress(5);
    setRenderStatus('starting');
    track('first_render_started', { template_id: template.id, mode });

    try {
      const res = await videoAPI.generateVideo({
        property_id: targetProperty.id,
        template_id: template.id,
        language: 'de',
      });
      const rid = res.data?.render_id;
      if (!rid) {
        // Edge case. Endpoint returned 200 with no render_id (e.g. a
        // queued-for-retry result). Without this guard pollRender(null)
        // would loop forever requesting /api/video/null/status against
        // a path that doesn't match any route.
        setPhase('error');
        setErrorMessage(
          t('firstRender.renderFailed')
          || 'Render konnte nicht gestartet werden. Bitte erneut versuchen.'
        );
        track('first_render_no_id', { template_id: template.id });
        return;
      }
      // Stash the demo-flag now (before polling). PollRender resolves
      // with /video/status data which doesn't carry is_first_render.
      setWasDemoRender(Boolean(res.data?.is_first_render));
      setRenderId(rid);
      pollRender(rid);
    } catch (err) {
      // The global 403 interceptor toasts the gate-message for
      // email_not_verified / card_on_file_required already. We just
      // pop the user back to `ready` so they can fix the gate (verify
      // email, add card) and retry the same template.
      const code = err.response?.data?.error_code;
      if (code === 'email_not_verified' || code === 'card_on_file_required') {
        setPhase('ready');
        setActiveTemplate(null);
        return;
      }
      if (code === 'subscription_limit_reached') {
        // 5/month free cap blown. Only happens on a returning user;
        // shove them at /pricing.
        setPhase('ready');
        setActiveTemplate(null);
        navigate('/pricing');
        return;
      }
      setPhase('error');
      setErrorMessage(
        err.response?.data?.detail
        || t('firstRender.renderFailed')
        || 'Render konnte nicht gestartet werden.'
      );
    }
  };

  // Polling loop with bounded retries. Same pattern as
  // PropertyDetailPage, simplified for the single-render context.
  // ── Manual-property handlers ─────────────────────────────────────
  // Upload images via the existing /properties/upload/image endpoint
  // (rate-limited + magic-byte validated server-side; quota tracked
  // per user). One-by-one upload so a single failure doesn't lose the
  // others. The endpoint returns { url } which we collect into form.images.
  const uploadImageFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingImages(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      const gatewayKey = process.env.REACT_APP_GATEWAY_KEY;
      if (gatewayKey) headers['X-Gateway-Key'] = gatewayKey;
      const newUrls = [];
      for (const f of Array.from(fileList).slice(0, 10)) {
        try {
          const fd = new FormData();
          fd.append('file', f);
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL || ''}/api/properties/upload/image`,
            { method: 'POST', headers, body: fd },
          );
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(err.detail || `${f.name}: Upload fehlgeschlagen`);
            continue;
          }
          const data = await res.json();
          if (data.url) newUrls.push(data.url);
        } catch (e) {
          toast.error(`${f.name}: ${e.message || 'Upload fehlgeschlagen'}`);
        }
      }
      if (newUrls.length) {
        setManualForm(p => ({ ...p, images: [...p.images, ...newUrls] }));
      }
    } finally {
      setUploadingImages(false);
    }
  };

  // AI fills the description textarea. Cap at 1 use per page mount.
  // Backend has its own 10/hour-per-IP rate limit so even a crafty user
  // editing localStorage can't burn through credits.
  const handleAIDescription = async () => {
    if (aiUsed || aiLoading) return;
    if (!manualForm.title.trim()) {
      toast.error(t('firstRender.aiNeedsTitle')
        || 'Bitte zuerst einen Titel eingeben. Die KI braucht einen Anhaltspunkt.');
      return;
    }
    setAiLoading(true);
    try {
      const highlights = manualForm.highlights
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const res = await aiAPI.draftDescription({
        title: manualForm.title,
        location: manualForm.location,
        price: manualForm.price,
        highlights,
        language: 'de',
      });
      const desc = res.data?.description || '';
      if (desc) {
        setManualForm(p => ({ ...p, description: desc }));
        setAiUsed(true);
        toast.success(t('firstRender.aiDescriptionDone') || 'Beschreibung erstellt. Du kannst sie noch anpassen.');
        track('first_render_ai_description');
      }
    } catch (err) {
      // 502 from the backend already toasted via the global interceptor
      // for structured errors. For plain 502s the user just sees this.
      toast.error(err.response?.data?.detail || t('firstRender.aiFailed') || 'KI nicht erreichbar.');
    } finally {
      setAiLoading(false);
    }
  };

  // Submit the manual form: POST /properties → store the result as the
  // current render target. After this the templates grid will fire
  // renders against the user's actual property instead of the demo.
  //
  // Validation here is stricter than the backend's PropertyCreate model
  // requires because the templates assume meaningful data: a 3-char
  // title produces an unwatchable video. The minimum-3-images cap
  // matches the lowest media_count of the curated FIRST_RENDER_TEMPLATE
  // set so every template the user can pick has enough to render.
  const handleSaveManualProperty = async (e) => {
    e?.preventDefault();
    const f = manualForm;
    const title = f.title.trim();
    const price = f.price.trim();
    const location = f.location.trim();
    if (!title || !price || !location) {
      toast.error(t('firstRender.fillRequired')
        || 'Titel, Preis und Adresse sind Pflicht.');
      return;
    }
    if (title.length < 8) {
      toast.error(t('firstRender.titleTooShort')
        || 'Titel ist zu kurz. Mind. 8 Zeichen, damit er im Video gut aussieht.');
      return;
    }
    if (f.images.length < 3) {
      toast.error(t('firstRender.imagesMinThree')
        || 'Bitte mind. 3 Bilder hochladen. Die meisten Vorlagen brauchen Auswahl.');
      return;
    }
    setSubmittingProperty(true);
    try {
      const highlights = f.highlights
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const res = await propertiesAPI.create({
        title: f.title.trim(),
        description: f.description.trim() || f.title.trim(),
        price: f.price.trim(),
        location: f.location.trim(),
        highlights,
        images: f.images,
      });
      setManualProperty(res.data);
      toast.success(t('firstRender.propertyCreated') || 'Immobilie gespeichert. Wähle jetzt eine Vorlage.');
      track('first_render_manual_property_created');
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('firstRender.propertyFailed')
        || 'Speichern fehlgeschlagen.'
      );
    } finally {
      setSubmittingProperty(false);
    }
  };

  // Willhaben import is delegated to the shared WillhabenImportFlow
  // component (see ../components/WillhabenImportFlow). It owns its own
  // URL/preview/selection state so we just hand it a callback and
  // get back the saved Property when the user commits.

  const pollRender = (rid) => {
    let attempts = 0;
    // Bumped from 60 (5min) to 120 (10min). Creatomate occasionally
    // takes 90s+ on the first render of the day when their workers
    // cold-start; a 5-min cap surfaces a misleading "timeout" error
    // when the render is actually about to succeed. 10 min is generous
    // enough that a real timeout indicates a genuine problem.
    const maxAttempts = 120;
    let consecutiveStatusFailures = 0;

    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        const r = await videoAPI.getVideoStatus(rid);
        if (!mountedRef.current) return;
        // Reset consecutive failure counter on a successful response
        consecutiveStatusFailures = 0;
        const s = r.data;
        setRenderStatus(s.status);
        // Smooth progress: backend's coarse value, with elapsed-time
        // bias so the bar always feels like it's moving.
        const live = s.progress ?? 0;
        const grown = Math.min(95, 5 + Math.min(attempts / maxAttempts, 1) * 90);
        setRenderProgress(Math.max(grown, live));

        if (s.status === 'succeeded') {
          setRenderProgress(100);
          setResultVideo({ ...s, render_id: rid });
          setPhase('done');
          markOnboardingComplete();
          // Best-effort: also tell the backend so a fresh login on a
          // different device skips the onboarding gate. The localStorage
          // mark is the fast path; this is the durable mirror.
          onboardingAPI.complete().catch(() => {/* best-effort */});
          track('first_render_succeeded', { template_id: activeTemplate?.id });
          return;
        }
        if (s.status === 'failed') {
          setPhase('error');
          setErrorMessage(
            s.error_message
            || t('firstRender.renderFailed')
            || 'Render fehlgeschlagen.'
          );
          track('first_render_failed', { template_id: activeTemplate?.id });
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          setPhase('error');
          setErrorMessage(
            t('firstRender.renderTimeout')
            || 'Render dauert länger als erwartet. Bitte Seite neu laden.'
          );
          return;
        }
        pollTimeoutRef.current = setTimeout(tick, 5000);
      } catch (e) {
        if (!mountedRef.current) return;
        // A single status-check failure is almost always transient
        // (CDN blip, brief 502, Mongo retrying). Don't surface it as
        // a hard error to the user. Only escalate after 5 consecutive
        // failures. Each attempt still counts toward maxAttempts so
        // the absolute timeout cap is honoured.
        consecutiveStatusFailures += 1;
        if (consecutiveStatusFailures >= 5) {
          setPhase('error');
          setErrorMessage(
            t('firstRender.statusCheckFailed')
            || 'Status konnte nicht geprüft werden.'
          );
          return;
        }
        attempts++;
        if (attempts >= maxAttempts) {
          setPhase('error');
          setErrorMessage(
            t('firstRender.renderTimeout')
            || 'Render dauert länger als erwartet. Bitte Seite neu laden.'
          );
          return;
        }
        pollTimeoutRef.current = setTimeout(tick, 5000);
      }
    };

    pollTimeoutRef.current = setTimeout(tick, 4000);
  };

  // ── RENDER ────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center">
        <div className="text-center">
          <RCLogo size="md" />
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin mx-auto mt-8" />
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-4">
        <Card className="glass border-[#b3543f]/40 max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-[#a34430]">{errorMessage}</p>
            <Button onClick={() => window.location.reload()} className="bg-[#1f4d3a] hover:bg-[#163a2c]">
              {t('firstRender.reload') || 'Neu laden'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render-in-progress full-screen overlay. The user can't navigate
  // away during this on purpose. We want them riveted to the magic.
  if (phase === 'rendering') {
    // currentProperty (NOT demoProperty) so the in-progress overlay
    // shows the cover of the property actually being rendered. Earlier
    // this hardcoded demoProperty, which made manual-mode renders look
    // like they were rendering the demo even though the API was
    // (correctly) firing against the user's own property.
    const propertyImg = currentProperty?.images?.[0];
    return (
      <div className="min-h-screen bg-[#f7f3ea] relative overflow-hidden flex items-center justify-center px-4">
        {/* Ambient gradient halos */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[28rem] h-[28rem] rounded-full bg-[#dfe8db] blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[28rem] h-[28rem] rounded-full bg-[#f0e6cf] blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-md w-full space-y-6 text-center">
          <div className="inline-flex p-4 rounded-2xl bg-[#e9efe7] border border-[#1f4d3a]/40 mb-2">
            <Wand2 className="w-8 h-8 text-[#1f4d3a] animate-pulse" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#23211a] leading-tight">
            {t('firstRender.renderingTitle') || 'Wir zaubern dein Video…'}
          </h1>

          {/* Cover preview pulsing in the center.
              Property title underneath confirms WHICH listing is being
              rendered. Eliminates the "wait, did it render the demo
              or my property?" confusion that earlier versions caused. */}
          {propertyImg && (
            <>
              <div className="relative aspect-[9/16] max-w-[200px] mx-auto rounded-xl overflow-hidden border-2 border-[#1f4d3a]/40 shadow-2xl">
                <img src={propertyImg} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#dfe8db] via-transparent to-[#8f6f3a]/30" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full p-3">
                    <Loader2 className="w-7 h-7 text-[#2e6b50] animate-spin" />
                  </div>
                </div>
              </div>
              {currentProperty?.title && (
                <p className="text-xs text-[#6f6a5c] truncate max-w-md mx-auto">
                  {mode === 'demo'
                    ? `${t('firstRender.demoBadge') || 'Beispiel'} · ${currentProperty.title}`
                    : currentProperty.title}
                </p>
              )}
            </>
          )}

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="h-2 w-full bg-[#e9e2d2] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#1f4d3a] via-[#1f4d3a] to-[#8f6f3a] transition-all duration-700 ease-out"
                style={{ width: `${renderProgress}%` }}
              />
            </div>
            <p className="text-xs text-[#8a8474]">
              {Math.round(renderProgress)}% · {activeTemplate?.name}
            </p>
          </div>

          {/* Rotating hype line */}
          <p className="text-sm text-[#4a463b] min-h-[2.5em]">
            {hypeLines[hypeIdx]}
          </p>
        </div>
      </div>
    );
  }

  // ── DONE: full-screen result + upgrade ladder ─────────────────────
  if (phase === 'done' && resultVideo) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] px-4 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className={`inline-flex p-3 rounded-2xl mb-3 ${
              wasDemoRender
                ? 'bg-[#e9efe7] border border-[#1f4d3a]/40'
                : 'bg-[#e4f0e8] border border-[#1e7a4f]/40'
            }`}>
              {wasDemoRender
                ? <Sparkles className="w-7 h-7 text-[#1f4d3a]" />
                : <CheckCircle2 className="w-7 h-7 text-[#1e7a4f]" />}
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold text-[#23211a] mb-2 leading-tight">
              {wasDemoRender
                ? (t('firstRender.demoTitle') || 'Das war dein Demo.')
                : (t('firstRender.doneTitle') || 'Magic, oder?')}
            </h1>
            <p className="text-base sm:text-lg text-[#6f6a5c]">
              {wasDemoRender
                ? (t('firstRender.demoSubtitle')
                  || 'Mach es zu deinem. Füge dein Foto und Branding hinzu, damit deine nächsten 5 Videos dich zeigen, nicht unser Demo-Modell.')
                : (t('firstRender.doneSubtitle')
                  || 'Das ist dein erstes Real-Content-Video. Komplett automatisch erstellt.')}
            </p>
          </div>

          {/* Auto-play result */}
          <div className={`relative mx-auto aspect-[9/16] max-w-[320px] rounded-2xl overflow-hidden border-2 shadow-2xl mb-6 bg-black ${
            wasDemoRender ? 'border-[#1f4d3a]/40' : 'border-[#1e7a4f]/40'
          }`}>
            <video
              src={resultVideo.video_url}
              poster={resultVideo.thumbnail_url}
              className="w-full h-full object-cover"
              controls
              autoPlay
              playsInline
              loop
              data-testid="first-render-result-video"
            />
            {wasDemoRender && (
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-[#1f4d3a]/95 text-white text-xs font-semibold tracking-wide uppercase shadow-lg">
                {t('firstRender.demoBadge') || 'Demo'}
              </div>
            )}
          </div>

          {/* Demo-only profile-completion banner. The empty profile is
              the *reason* the demo realtor-photo / no-brand is in this
              video. We turn that confusion into the obvious next step.
              Renders ABOVE the upgrade ladder so it doesn't compete with
              paid CTAs for the first-time user's attention. */}
          {wasDemoRender && (
            <div className="mb-8 p-5 rounded-2xl border border-[#1f4d3a]/40 bg-[#f0f3ec]">
              <div className="flex items-start gap-4">
                <div className="shrink-0 p-2.5 rounded-xl bg-[#e9efe7] border border-[#1f4d3a]/40">
                  <UserCircle2 className="w-6 h-6 text-[#1f4d3a]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-[#23211a] mb-1">
                    {t('firstRender.profileNudgeTitle')
                      || 'Dein Foto und Branding fehlen noch'}
                  </h3>
                  <p className="text-sm text-[#2e6b50]/80 mb-4 leading-relaxed">
                    {t('firstRender.profileNudgeBody')
                      || 'Im Demo siehst du unser Standard-Maklerfoto. Lade dein Profilbild, Logo und Telefonnummer hoch. Dann zeigt jedes deiner 5 Free-Videos dich.'}
                  </p>
                  <Button
                    onClick={() => {
                      track('first_render_profile_nudge_click');
                      navigate('/profile');
                    }}
                    className="bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white"
                    data-testid="first-render-profile-nudge"
                  >
                    {t('firstRender.profileNudgeCta') || 'Profil vervollständigen'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade ladder. Three feature-led cards. The user just
              experienced the magic; this is the most receptive moment
              we'll ever have to pitch the paid features. Order matches
              urgency: Justimmo (the "do this for ALL my listings"
              dopamine), Auto-Post (the "while I sleep" dopamine), Team
              (only for agencies, smaller audience). */}
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#23211a] mb-1">
              {t('firstRender.nextTitle') || 'Was möchtest du als nächstes?'}
            </h2>
            <p className="text-sm text-[#8a8474]">
              {t('firstRender.nextSubtitle')
                || 'So sieht es mit deinen echten Inseraten aus.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <UpgradeCard
              icon={Database}
              accent="violet"
              title={t('firstRender.ladderJustimmoTitle') || 'Justimmo verbinden'}
              body={t('firstRender.ladderJustimmoBody')
                || 'Importiere deine echten Inserate automatisch. Mit Autopilot unbegrenzt.'}
              cta={t('firstRender.ladderJustimmoCta') || 'Mehr erfahren'}
              onClick={() => navigate('/pricing')}
              testId="ladder-justimmo"
            />
            <UpgradeCard
              icon={Zap}
              accent="violet"
              title={t('firstRender.ladderAutoPostTitle') || 'Auto-Post aktivieren'}
              body={t('firstRender.ladderAutoPostBody')
                || 'KI postet täglich für dich auf Instagram, TikTok & Co. Mit Vorschau-Mail.'}
              cta={t('firstRender.ladderAutoPostCta') || 'Autopilot ansehen'}
              onClick={() => navigate('/pricing')}
              testId="ladder-autopost"
              highlight
            />
            <UpgradeCard
              icon={Users}
              accent="teal"
              title={t('firstRender.ladderTeamTitle') || 'Team starten'}
              body={t('firstRender.ladderTeamBody')
                || 'Mehrere Makler unter einer Abrechnung. Ab €179 / Monat.'}
              cta={t('firstRender.ladderTeamCta') || 'Team-Plan'}
              onClick={() => navigate('/pricing')}
              testId="ladder-team"
            />
          </div>

          <div className="text-center space-y-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#d8d0bd] text-[#4a463b]"
              data-testid="first-render-to-dashboard"
            >
              {t('firstRender.toDashboard') || 'Zum Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-[#8a8474]">
              {wasDemoRender
                ? (t('firstRender.demoQuotaHint')
                  || 'Demo zählt nicht. Du hast noch alle 5 Free-Renders übrig.')
                : (t('firstRender.freeQuotaHint')
                  || 'Du hast noch 4 weitere Free-Renders übrig. Probiere weitere Vorlagen aus!')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── READY: the main pick-a-template surface ───────────────────────
  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      {/* Ambient halos */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#f0e6cf] rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <RCLogo size="md" />
          <button
            type="button"
            onClick={() => {
              markOnboardingComplete();
              onboardingAPI.complete().catch(() => {/* best-effort backend sync */});
              navigate('/dashboard');
            }}
            className="text-xs text-[#8a8474] hover:text-[#4a463b] transition"
            data-testid="first-render-skip"
          >
            {t('firstRender.skip') || 'Später machen'}
          </button>
        </div>

        {/* Hype hero */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#e9efe7] border border-[#1f4d3a]/30 text-xs font-medium text-[#2e6b50] mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {t('firstRender.heroBadge') || '60-Sekunden-Test'}
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#23211a] mb-4 leading-tight">
            {t('firstRender.heroTitle') || 'Mach jetzt dein erstes Video.'}
          </h1>
          <p className="text-base sm:text-lg text-[#6f6a5c] max-w-2xl mx-auto">
            {t('firstRender.heroSubtitle')
              || 'Wähle eine Vorlage. Wir rendern es mit unserer Beispiel-Immobilie. Du musst nichts hochladen, nichts einstellen.'}
          </p>
        </div>

        {/* Mode toggle: three pills. Beispiel (auto-loaded), Willhaben
            (paste-and-import), Eigene Bilder (manual form).
            Default = Beispiel keeps zero-friction for new users; the
            other two are for Maklers who want to test against their
            real listing. Willhaben put first because it's by far the
            most common path for Austrian agents. */}
        <div className="mb-5 flex justify-center">
          <div className="inline-flex bg-white border border-[#e3dccb] rounded-full p-1 gap-1 flex-wrap justify-center">
            <button
              type="button"
              onClick={() => setMode('demo')}
              className={`text-xs sm:text-sm px-4 py-1.5 rounded-full transition ${
                mode === 'demo'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="first-render-mode-demo"
            >
              <Sparkles className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('firstRender.modeDemo') || 'Beispiel verwenden'}
            </button>
            <button
              type="button"
              onClick={() => setMode('willhaben')}
              className={`text-xs sm:text-sm px-4 py-1.5 rounded-full transition ${
                mode === 'willhaben'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="first-render-mode-willhaben"
            >
              <LinkIcon className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('firstRender.modeWillhaben') || 'Von Willhaben'}
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`text-xs sm:text-sm px-4 py-1.5 rounded-full transition ${
                mode === 'manual'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="first-render-mode-manual"
            >
              <Upload className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('firstRender.modeManual') || 'Eigene Bilder'}
            </button>
          </div>
        </div>

        {/* Property card or input UI, depending on mode + whether
            the user has already saved/imported. */}
        {mode === 'demo' && demoProperty && (
          <PropertyCard property={demoProperty} isDemo t={t} />
        )}
        {mode === 'manual' && manualProperty && (
          <PropertyCard
            property={manualProperty}
            isDemo={false}
            t={t}
            onEdit={() => setEditingProperty(manualProperty)}
          />
        )}
        {mode === 'manual' && !manualProperty && (
          <ManualPropertyForm
            t={t}
            form={manualForm}
            setForm={setManualForm}
            uploadingImages={uploadingImages}
            onUpload={uploadImageFiles}
            fileInputRef={fileInputRef}
            aiLoading={aiLoading}
            aiUsed={aiUsed}
            onAIDescription={handleAIDescription}
            submitting={submittingProperty}
            onSubmit={handleSaveManualProperty}
          />
        )}
        {mode === 'willhaben' && willhabenProperty && (
          <PropertyCard
            property={willhabenProperty}
            isDemo={false}
            t={t}
            fromWillhaben
            onEdit={() => setEditingProperty(willhabenProperty)}
          />
        )}
        {mode === 'willhaben' && !willhabenProperty && (
          <WillhabenImportFlow
            onImported={(property) => {
              setWillhabenProperty(property);
              track('first_render_willhaben_imported', { listing_id: property.id });
            }}
          />
        )}

        {/* Templates. Autoplay-on-hover demo videos. Click → render.
            Dimmed + un-clickable when there's no current property (manual
            mode, form not yet submitted) so the user can't accidentally
            trigger a render against nothing. */}
        <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#23211a]">
              {t('firstRender.pickTemplate') || 'Wähle eine Vorlage'}
            </h2>
            {currentProperty && renderableTemplates.length > 0 && (
              <p className="text-[11px] text-[#8a8474] mt-1">
                {t('firstRender.templateCount', { count: renderableTemplates.length })
                  || `${renderableTemplates.length} Vorlagen verfügbar`}
              </p>
            )}
          </div>
          <p className="text-xs text-[#8a8474]">
            {currentProperty
              ? (t('firstRender.clickHint') || 'Klick. Wir rendern sofort.')
              : mode === 'willhaben'
                ? (t('firstRender.willhabenImportFirst') || 'Importiere zuerst dein Willhaben-Inserat oben.')
                : (t('firstRender.fillFormFirst') || 'Speichere zuerst deine Immobilie oben.')}
          </p>
        </div>
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 transition-opacity ${
            currentProperty ? '' : 'opacity-40 pointer-events-none'
          }`}
          aria-disabled={!currentProperty}
        >
          {renderableTemplates.map((tpl) => (
            <Card
              key={tpl.id}
              data-testid={`first-render-template-${tpl.id}`}
              onClick={() => handleRender(tpl)}
              className="group glass border-[#e3dccb] hover:border-[#1f4d3a] transition-all duration-300 cursor-pointer overflow-hidden md:hover:scale-105 hover:shadow-2xl active:scale-95 md:active:scale-105"
            >
              <div className="relative h-[280px] sm:h-[340px] lg:h-[380px] bg-[#fffdf7] overflow-hidden">
                {tpl.demo_video && !brokenDemoVideos.has(tpl.id) ? (
                  <video
                    src={tpl.demo_video}
                    className="w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onError={() =>
                      setBrokenDemoVideos(prev => new Set(prev).add(tpl.id))
                    }
                  />
                ) : (
                  <div className="relative w-full h-full">
                    <img
                      src={tpl.thumbnail}
                      alt={tpl.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {/* Image-count badge. Top-left corner, tells the user
                    how many photos this template will use. Helps them
                    pick a template that matches their image set; no
                    surprise "you need more photos" later. */}
                {Number(tpl.media_count) > 0 && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#f7f3ea]/90 backdrop-blur-sm text-[#23211a] border border-[#d8d0bd]">
                    <ImageIcon className="w-3 h-3" />
                    {tpl.media_count}
                  </span>
                )}
                {/* Hover/touch overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 sm:pb-6 pointer-events-none">
                  <Button
                    size="sm"
                    className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary text-xs sm:text-sm"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {t('firstRender.useTemplate') || 'Diese rendern'}
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="text-base font-bold text-[#23211a] group-hover:text-[#1f4d3a] transition-colors mb-1">
                  {tpl.name}
                </h3>
                <p className="text-xs text-[#8a8474] line-clamp-2">{tpl.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No-renderable-template fallback. Fires only when a manual
            property has fewer images than every available template
            requires (e.g. user uploaded 2 images and the lowest-bar
            template needs 3+). Tells them to add more images instead
            of leaving them with an empty grid. */}
        {currentProperty && renderableTemplates.length === 0 && (
          <Card className="glass border-[#c7a869] bg-[#c7a869]/5">
            <CardContent className="p-5 text-center">
              <p className="text-sm text-[#9a6d1f]">
                {t('firstRender.noTemplatesForProperty')
                  || 'Keine Vorlage passt zu deiner Bildanzahl. Lade ein paar mehr Fotos hoch, dann öffnen sich die Vorlagen.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Power-user escape hatch */}
        <div className="text-center mt-10">
          <p className="text-sm text-[#8a8474]">
            {t('firstRender.orUploadOwnPrefix')
              || 'Lieber gleich eine eigene Immobilie testen?'}{' '}
            <button
              onClick={() => {
                markOnboardingComplete();
                onboardingAPI.complete().catch(() => {/* best-effort */});
                navigate('/properties');
              }}
              className="text-[#1f4d3a] hover:text-[#2e6b50] underline"
              data-testid="first-render-upload-own"
            >
              {t('firstRender.orUploadOwnLink') || 'Eigene Immobilie anlegen'}
            </button>
          </p>
        </div>
      </div>

      {/* Quick-edit modal. Overlays the whole page so the user can
          tweak the imported/manual property's title/description/price
          before clicking a template. Saves via PUT /api/properties/{id}
          and the parent state gets updated so the rendered video uses
          the corrected fields. */}
      {editingProperty && (
        <PropertyQuickEditModal
          property={editingProperty}
          onClose={() => setEditingProperty(null)}
          onSaved={(updated) => {
            // Update whichever slot this came from. We compare by id –
            // both manualProperty + willhabenProperty are nullable.
            if (manualProperty && updated.id === manualProperty.id) {
              setManualProperty(updated);
            }
            if (willhabenProperty && updated.id === willhabenProperty.id) {
              setWillhabenProperty(updated);
            }
            setEditingProperty(null);
          }}
        />
      )}
    </div>
  );
}

/* ─── small inline component for the upgrade ladder cards ────────── */

/* ─── Property card. Used for both demo + saved manual property ──── */

function PropertyCard({ property, isDemo, t, fromWillhaben, onEdit }) {
  const cover = property.images?.[0];
  return (
    <Card className="glass border-[#1f4d3a]/30 mb-8 overflow-hidden">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 sm:grid-cols-3">
          <div className="relative aspect-video sm:aspect-auto bg-[#fffdf7]">
            {cover ? (
              <img src={cover} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-10 h-10 text-[#b5ad99]" />
              </div>
            )}
            {isDemo && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1f4d3a]/90 text-white font-semibold">
                <Sparkles className="w-3 h-3" />
                {t('firstRender.demoBadge') || 'Beispiel'}
              </span>
            )}
            {!isDemo && fromWillhaben && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#c7a869] text-[#23211a] font-semibold">
                <LinkIcon className="w-3 h-3" />
                {t('firstRender.fromWillhabenBadge') || 'Aus Willhaben'}
              </span>
            )}
            {!isDemo && !fromWillhaben && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1e7a4f]/90 text-white font-semibold">
                <CheckCircle2 className="w-3 h-3" />
                {t('firstRender.yourProperty') || 'Deine Immobilie'}
              </span>
            )}
          </div>
          <div className="sm:col-span-2 p-5">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h2 className="text-xl font-bold text-[#23211a]">{property.title}</h2>
              {/* Edit affordance. Only for the user's own properties.
                  Particularly useful right after a Willhaben import where
                  the auto-scraped title is often SEO-spammy. */}
              {!isDemo && onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="shrink-0 inline-flex items-center gap-1 text-xs text-[#6f6a5c] hover:text-[#1f4d3a] transition px-2 py-1 rounded hover:bg-[#efe9db]"
                  data-testid="property-card-edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {t('firstRender.editProperty') || 'Bearbeiten'}
                </button>
              )}
            </div>
            <p className="text-base text-[#1f4d3a] font-semibold mb-1">{property.price}</p>
            <p className="text-sm text-[#6f6a5c] mb-3">{property.location}</p>
            <div className="flex flex-wrap gap-1.5">
              {(property.highlights || []).slice(0, 4).map((h, i) => (
                <span
                  key={i}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-[#e9e2d2] text-[#4a463b] border border-[#d8d0bd]"
                >
                  {h}
                </span>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Manual-property form (mode='manual', not yet submitted) ──────── */

function ManualPropertyForm({
  t, form, setForm, uploadingImages, onUpload, fileInputRef,
  aiLoading, aiUsed, onAIDescription, submitting, onSubmit,
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onUpload(e.dataTransfer.files);
  };
  const removeImage = (idx) => {
    setForm(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  // Visual signals so the user knows when each field is "good enough"
  // for a decent video. Hard-fail in the submit handler stops bad data;
  // these are softer hints to encourage richer input.
  const titleOk = form.title.trim().length >= 8;
  const imagesOk = form.images.length >= 3;
  const hasHighlights = form.highlights.split(',').map(s => s.trim()).filter(Boolean).length > 0;

  return (
    <Card className="glass border-[#1f4d3a]/30 mb-8">
      <CardContent className="p-5 sm:p-6">
        {/* "What shows up in the video" guidance. At the top so the
            user knows which fields will appear on-screen and which
            are optional flavor. Kept short; full guidance in tooltips
            under each field. */}
        <div className="rounded-lg bg-[#f0f3ec] border border-[#1f4d3a]/20 p-3 mb-5 text-xs">
          <p className="font-medium text-[#2e6b50] mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            {t('firstRender.formGuidanceTitle') || 'So nutzt das Video deine Eingaben'}
          </p>
          <ul className="space-y-0.5 text-[#6f6a5c] leading-relaxed">
            <li>• <span className="text-[#4a463b]">{t('firstRender.formGuidanceImages') || 'Bilder'}</span>. Werden direkt im Video gezeigt (Auswahl: 3-7)</li>
            <li>• <span className="text-[#4a463b]">{t('firstRender.formGuidanceTitle2') || 'Titel'}</span>. Als Headline + im KI-Voiceover</li>
            <li>• <span className="text-[#4a463b]">{t('firstRender.formGuidancePrice') || 'Preis + Adresse'}</span>. Overlays auf den meisten Vorlagen</li>
            <li>• <span className="text-[#4a463b]">{t('firstRender.formGuidanceHighlights') || 'Highlights'}</span>. Kleine Pillen / Aufzählung in einigen Templates</li>
          </ul>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="manual-property-form">
          {/* Image dropzone */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-2 flex items-center justify-between gap-2">
              <span>{t('firstRender.imagesLabel') || 'Bilder'} *</span>
              <span className={`text-[11px] font-normal ${imagesOk ? 'text-[#1e7a4f]' : 'text-[#9a6d1f]'}`}>
                {form.images.length === 0
                  ? (t('firstRender.imagesNeed3') || 'Mind. 3 Bilder')
                  : imagesOk
                    ? (t('firstRender.imagesOk', { count: form.images.length }) || `${form.images.length} hochgeladen ✓`)
                    : (t('firstRender.imagesNeedMore', { count: 3 - form.images.length }) || `noch ${3 - form.images.length} Bild(er) nötig`)}
              </span>
            </label>
            <div
              className={`relative border-2 border-dashed rounded-xl text-center p-5 transition-all cursor-pointer ${
                dragOver
                  ? 'border-[#1f4d3a] bg-[#e9efe7]'
                  : 'border-[#d8d0bd] hover:border-[#1f4d3a]/40 bg-[#f7f3ea]/30'
              } ${uploadingImages ? 'pointer-events-none opacity-60' : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => onUpload(e.target.files)}
                className="hidden"
              />
              {uploadingImages ? (
                <div className="flex flex-col items-center gap-2 py-3">
                  <Loader2 className="w-6 h-6 text-[#1f4d3a] animate-spin" />
                  <span className="text-sm text-[#6f6a5c]">
                    {t('firstRender.uploading') || 'Wird hochgeladen…'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-3">
                  <div className="p-2.5 bg-[#e9e2d2] rounded-full">
                    <Upload className="w-5 h-5 text-[#4a463b]" />
                  </div>
                  <p className="text-sm text-[#23211a]">
                    {t('firstRender.dragOrClick') || 'Bilder hierhin ziehen oder klicken'}
                  </p>
                  <p className="text-xs text-[#8a8474]">
                    {t('firstRender.imageHint') || 'JPG, PNG, WebP · max 5 MB pro Datei · 3-7 Bilder ideal'}
                  </p>
                </div>
              )}
            </div>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                {form.images.map((url, i) => (
                  <div
                    key={i}
                    className="relative aspect-square rounded-lg overflow-hidden bg-[#fffdf7] border border-[#e3dccb] group"
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 text-[9px] px-1 py-0.5 rounded bg-[#1f4d3a] text-white">
                        Cover
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 p-0.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition"
                      aria-label={t('common.remove') || 'Entfernen'}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-1.5 flex items-center justify-between gap-2">
              <span>{t('firstRender.titleLabel') || 'Titel'} *</span>
              <span className={`text-[11px] font-normal ${titleOk ? 'text-[#1e7a4f]' : form.title.length === 0 ? 'text-[#8a8474]' : 'text-[#9a6d1f]'}`}>
                {form.title.length === 0
                  ? (t('firstRender.titleNeed8') || 'Mind. 8 Zeichen')
                  : titleOk
                    ? '✓'
                    : (t('firstRender.titleShort', { remaining: 8 - form.title.trim().length })
                       || `noch ${8 - form.title.trim().length}`)}
              </span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder={t('firstRender.titlePlaceholder') || 'Schönes Familienhaus in Wien'}
              className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-base text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
              required
              data-testid="manual-form-title"
            />
            <p className="text-[11px] text-[#8a8474] mt-1">
              {t('firstRender.titleHelp') || 'Erscheint als Headline + wird vom KI-Voiceover gesprochen.'}
            </p>
          </div>

          {/* Price + location side-by-side on tablet+ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('firstRender.priceLabel') || 'Preis'} *
              </label>
              <input
                type="text"
                value={form.price}
                onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                placeholder="€ 450.000"
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-base text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('firstRender.locationLabel') || 'Adresse'} *
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))}
                placeholder="1010 Wien"
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-base text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                required
              />
            </div>
          </div>

          {/* Highlights. Single comma-separated field for simplicity */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-1.5">
              {t('firstRender.highlightsLabel') || 'Highlights (mit Komma trennen)'}
            </label>
            <input
              type="text"
              value={form.highlights}
              onChange={(e) => setForm(p => ({ ...p, highlights: e.target.value }))}
              placeholder={t('firstRender.highlightsPlaceholder')
                || '4 Zimmer, 130 m², Garten, Garage'}
              className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-base text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
            />
          </div>

          {/* Description with AI button */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#23211a]">
                {t('firstRender.descriptionLabel') || 'Beschreibung'}
              </label>
              <button
                type="button"
                onClick={onAIDescription}
                disabled={aiLoading || aiUsed || !form.title.trim()}
                className="inline-flex items-center gap-1.5 text-xs text-[#1f4d3a] hover:text-[#2e6b50] disabled:text-[#9c9585] disabled:cursor-not-allowed transition"
                data-testid="manual-form-ai-description"
              >
                {aiLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : aiUsed ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1e7a4f]" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                {aiUsed
                  ? (t('firstRender.aiDone') || 'KI-Vorschlag eingefügt')
                  : (t('firstRender.aiSuggest') || 'KI-Beschreibung erstellen')}
              </button>
            </div>
            <textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              rows={4}
              placeholder={t('firstRender.descriptionPlaceholder')
                || 'Charmante Immobilie in begehrter Lage… (oder klick „KI-Beschreibung erstellen")'}
              className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-base text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
            />
            <p className="text-xs text-[#8a8474] mt-1">
              {t('firstRender.aiHint') || 'KI-Vorschlag ist optional. Du kannst danach noch alles anpassen. Eine Generierung pro Sitzung.'}
            </p>
          </div>

          {/* Submit. Disabled until the hard-required fields are filled
              AND have enough content to render a watchable video. Match
              the rules enforced server-side + in handleSaveManualProperty. */}
          {(() => {
            const allValid = (
              titleOk
              && form.price.trim().length > 0
              && form.location.trim().length > 0
              && imagesOk
            );
            return (
              <Button
                type="submit"
                disabled={submitting || uploadingImages || !allValid}
                className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white disabled:bg-[#e9e2d2] disabled:text-[#8a8474]"
                data-testid="manual-form-submit"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Pencil className="w-4 h-4 mr-2" />
                )}
                {t('firstRender.saveProperty') || 'Speichern und Vorlage wählen'}
              </Button>
            );
          })()}
        </form>
      </CardContent>
    </Card>
  );
}

function UpgradeCard({ icon: Icon, accent, title, body, cta, onClick, testId, highlight }) {
  const accentMap = {
    violet: {
      ring: 'border-[#1f4d3a]/40 hover:border-[#1f4d3a]',
      iconBg: 'bg-[#e9efe7]',
      iconColor: 'text-[#1f4d3a]',
      btn: 'bg-[#1f4d3a] hover:bg-[#163a2c] text-white',
    },
    teal: {
      ring: 'border-[#c7a869]/60 hover:border-[#c7a869]',
      iconBg: 'bg-[#f5eede]',
      iconColor: 'text-[#8f6f3a]',
      btn: 'bg-[#8f6f3a] hover:bg-[#75592e] text-white',
    },
  };
  const c = accentMap[accent] || accentMap.violet;
  return (
    <Card
      className={`glass ${c.ring} transition-all duration-300 ${highlight ? 'md:scale-105 md:shadow-2xl' : ''}`}
      data-testid={testId}
    >
      <CardContent className="p-5 flex flex-col h-full">
        <div className={`p-2.5 rounded-lg ${c.iconBg} w-fit mb-3`}>
          <Icon className={`w-5 h-5 ${c.iconColor}`} />
        </div>
        <h3 className="font-semibold text-[#23211a] mb-1">{title}</h3>
        <p className="text-sm text-[#6f6a5c] mb-4 flex-1">{body}</p>
        <Button
          size="sm"
          onClick={onClick}
          className={`w-full ${c.btn}`}
        >
          {cta}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}
